import { VelocityClient, User } from '@velocity-exchange/sdk';
import { timedPromise } from '@velocity-exchange/common';
import { dlog } from 'src/dev';

const TX_PREP_TIME_METRIC_START_TIME_CACHE = new Map<string, number>();
import {
	VelocityClientTxSignedHandler,
	PREVIOUS_TX_SIGNED_HANDLER,
	TxMetricsOpts,
} from '../types';

// Wall-clock breathing room for the RPC to settle before refetching. Not a
// slot: shortening it with the slot length would race the refetch.
const POST_TX_REFETCH_DELAY_MS = 400;

export const updateAfterTx = (velocityClient: VelocityClient, user: User) => {
	setTimeout(() => {
		// velocityClient.fetchAccounts() already refetches every user it holds.
		if (![...velocityClient.users.values()].includes(user)) {
			user.fetchAccounts();
		}
		velocityClient.fetchAccounts();
	}, POST_TX_REFETCH_DELAY_MS);
};

export async function sendTxAndRecordConfirmationTime<
	T extends Promise<unknown>,
>(
	velocityClient: VelocityClient,
	txPromise: T,
	onSigned?: VelocityClientTxSignedHandler,
	opts?: TxMetricsOpts
) {
	let handler: VelocityClientTxSignedHandler;
	const signaturePromise = new Promise<void>((resolve) => {
		handler = (signedTxs) => {
			resolve();
			onSigned?.(signedTxs);
		};
		addTxSignedEventHandler(velocityClient, handler);
	});
	const txSignedEventEmitter = velocityClient.metricsEventEmitter;

	const txSignatureTimePromise = timedPromise(signaturePromise);
	const txConfirmationTimePromise = timedPromise(txPromise);

	txPromise.catch(() => {
		removeTxSignedEventHandler(velocityClient, handler);
	});

	if (opts?.measurePreSignTime) {
		let preSignHandler: () => void;
		const preSignPromise = new Promise<void>((resolve) => {
			preSignHandler = resolve;
			txSignedEventEmitter.on('preTxSigned', preSignHandler);
		});
		const preSignTimePromise = timedPromise(preSignPromise);
		preSignTimePromise
			.then((result) => {
				const cachedStartTime = TX_PREP_TIME_METRIC_START_TIME_CACHE.get(
					opts.measurePreSignTimeMetricKey ?? ''
				);
				const shouldAdjustMeasuredTime = !!cachedStartTime;

				const metricTime = shouldAdjustMeasuredTime
					? Date.now() - cachedStartTime
					: result.promiseTime;

				const metricKey = opts.measurePreSignTimeMetricKey ?? 'unlabelled';

				dlog(`ui_metrics`, `pre_sign_time_measurement ${metricTime}ms`, {
					metricKey,
					promiseTime: result.promiseTime,
					metricTime: metricTime,
					delta: metricTime - result.promiseTime,
					shouldAdjustMeasuredTime,
				});
			})
			.finally(() => {
				if (preSignHandler) {
					txSignedEventEmitter.off('preTxSigned', preSignHandler);
				}
			});
	}

	Promise.all([txSignatureTimePromise, txConfirmationTimePromise]).then(
		(results) => {
			const txSignatureTime = results[0].promiseTime;
			const txConfirmationTime = results[1].promiseTime;

			const actualConfirmationTime = txConfirmationTime - txSignatureTime;

			if (actualConfirmationTime <= 0) {
				// Caught an edge case once where the txConfirmationTime was less than the txSignatureTime. Don't see how this is possible not how we can "gracefully" handle it, so just going to skip logging this transaction time.
				dlog(`ui_metrics`, `bug::confirmation_time_less_than_sig_time`, {
					txSignatureTime,
					txConfirmationTime,
					diff: actualConfirmationTime,
				});
				return;
			}

			const formattedPromiseTime = +(actualConfirmationTime / 1000).toFixed(1);

			dlog(
				`ui_metrics`,
				`confirmation_time_measurement ${formattedPromiseTime}s`,
				{
					txSignatureTime,
					txConfirmationTime,
					diff: actualConfirmationTime,
				}
			);

			removeTxSignedEventHandler(velocityClient, handler);
		}
	);

	return txPromise;
}

const removeTxSignedEventHandler = (
	velocityClient: VelocityClient,
	handler: VelocityClientTxSignedHandler
) => {
	velocityClient.metricsEventEmitter.off('txSigned', handler);
	PREVIOUS_TX_SIGNED_HANDLER.current = undefined;
};

const addTxSignedEventHandler = (
	velocityClient: VelocityClient,
	handler: VelocityClientTxSignedHandler
) => {
	if (PREVIOUS_TX_SIGNED_HANDLER.current) {
		removeTxSignedEventHandler(
			velocityClient,
			PREVIOUS_TX_SIGNED_HANDLER.current
		);
	}

	velocityClient.metricsEventEmitter.on('txSigned', handler);
	PREVIOUS_TX_SIGNED_HANDLER.current = handler;
};
